import { Box, Collapse, Grid, IconButton, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { NumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { ChevronDownIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import {
  getGetModelingAvailableAmountQueryKey,
  getGetModelingInsuranceIncreasedAmountQueryKey,
  useGetModelingInsuranceExpenses,
  useGetModelingInsuranceIncreasedAmount,
  useUpdateModelingInsuranceIncreasedAmount,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrencyWithoutCode } from "Infrastructure/Utils/CurrencyUtils";
import { useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { AmountInputCard } from "./AmountInputCard";

export const ModelingInsurance = () => {
  const { t } = useResource();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: insuranceExpenses } = useGetModelingInsuranceExpenses(analysisId, versionId);
  const { data: increasedInsuranceAmount } = useGetModelingInsuranceIncreasedAmount(analysisId, versionId);

  const [insuranceExpensesInputValue, setInsuranceExpensesInputValue] = useState(
    Number(increasedInsuranceAmount?.amount?.value ?? 0).toString(),
  );

  useEffect(() => {
    if (increasedInsuranceAmount) {
      setInsuranceExpensesInputValue(Number(increasedInsuranceAmount.amount?.value ?? 0).toString());
    }
  }, [increasedInsuranceAmount]);

  const { mutate } = useUpdateModelingInsuranceIncreasedAmount();

  return (
    <Box>
      <Box
        onClick={() => setIsOpen(!isOpen)}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ cursor: "pointer", userSelect: "none" }}
      >
        <Typography fontWeight={600}>{t(Resources.AnalysisDetail.Modeling.AssetAndPersonalInsuranceCoverage)}</Typography>
        <IconButton
          disableRipple
          sx={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
          }}
        >
          <ChevronDownIcon />
        </IconButton>
      </Box>

      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Box mt={Spacing.M}>
          <Grid columnSpacing={Spacing.XXXL} container>
            <Grid size={4} offset={8}>
              <Stack spacing={Spacing.M}>
                <AmountInputCard>
                  <Stack spacing={Spacing.M}>
                    <Typography variant="body2" fontWeight={600}>
                      {t(Resources.AnalysisDetail.Modeling.ExistingInsuranceExpenses)}
                    </Typography>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">{t(Resources.Common.Total)}</Typography>
                      <Box display="flex">
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrencyWithoutCode(insuranceExpenses?.amount.value)}
                        </Typography>
                        &nbsp;
                        <Typography variant="body2">{t(Resources.Common.CZKSuffix)}</Typography>
                      </Box>
                    </Box>
                  </Stack>
                </AmountInputCard>

                <AmountInputCard>
                  <Box display="flex">
                    <Box width={220}>
                      <Typography variant="body2" fontWeight={600}>
                        {t(Resources.AnalysisDetail.Modeling.IncreasedInsuranceExpenses)}
                      </Typography>
                    </Box>
                    <Box display="flex">
                      <NumberField
                        disabled={readOnly}
                        value={Number(insuranceExpensesInputValue).toString()}
                        valueType="text"
                        min={0}
                        thousandsSeparator=" "
                        unit={t(Resources.Common.CZKSuffix)}
                        slotProps={{ htmlInput: { "aria-label": t(Resources.AnalysisDetail.Modeling.IncreasedInsuranceExpenses) } }}
                        onChange={(value) => {
                          setInsuranceExpensesInputValue(value);
                        }}
                        onBlur={() => {
                          mutate(
                            {
                              analysisId,
                              versionId,
                              data: {
                                amount: {
                                  value: Number(insuranceExpensesInputValue),
                                  currencyCode: "CZK",
                                  frequency: AmountFrequency.monthly,
                                },
                              },
                            },
                            {
                              onSuccess: () => {
                                toast.success(t(Resources.AnalysisDetail.Modeling.IncreasedInsuranceExpensesUpdated));
                                queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAmountQueryKey(analysisId, versionId) });
                                queryClient.invalidateQueries({
                                  queryKey: getGetModelingInsuranceIncreasedAmountQueryKey(analysisId, versionId),
                                });
                              },
                              onError: () => toast.error(t(Resources.AnalysisDetail.Modeling.IncreasedInsuranceExpensesError)),
                            },
                          );
                        }}
                      />
                    </Box>
                  </Box>
                </AmountInputCard>

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600} mt={Spacing.M}>
                    {t(Resources.Common.Total)}
                  </Typography>
                  <Box display="flex" mt={Spacing.M} alignItems="center">
                    <Typography variant="body2" fontWeight={500}>
                      {formatCurrencyWithoutCode(
                        Number(insuranceExpenses?.amount.value ?? 0) + Number(increasedInsuranceAmount?.amount?.value ?? 0),
                      )}
                    </Typography>
                    &nbsp;
                    <Typography variant="caption" color="secondary">
                      {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
};
